import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import SEOHead from '@/components/feature/SEOHead';
import { getVideoById, videos } from '@/mocks/videos';

const categoryNames: Record<string, string> = {
  entertainment: '娱乐综艺', education: '教育学习', technology: '科技数码',
  lifestyle: '生活美食', sports: '体育竞技', music: '音乐舞蹈',
  gaming: '游戏动漫', documentary: '纪录纪实', fashion: '时尚美妆', kids: '少儿乐园',
};

const formatViews = (n: number) => {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return `${n}`;
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id || '');

  if (!video) {
    return (
      <>
        <SEOHead title="视频未找到 - 91免费视频" description="您要查看的视频不存在或已被移除。" keywords="91免费视频" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-14 md:pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-video-off-line text-3xl text-foreground-300"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">视频未找到</h1>
              <p className="text-foreground-500 mb-6">您要查看的视频不存在或已被移除</p>
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 4);

  const formatViewsFull = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万次观看`;
    return `${n}次观看`;
  };

  return (
    <>
      <SEOHead
        title={`${video.title} - 91免费视频`}
        description={video.description}
        keywords={`91免费视频,${video.tags.join(',')},${video.title},免费视频在线观看`}
        canonicalUrl={`${window.location.origin}${__BASE_PATH__}/article/${video.id}`}
        ogType="article"
        ogTitle={video.title}
        ogDescription={video.description}
        ogImage={video.thumbnail}
        schemaData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: video.title,
          description: video.description,
          image: video.thumbnail,
          datePublished: video.date,
          author: {
            '@type': 'Organization',
            name: '91免费视频',
          },
          publisher: {
            '@type': 'Organization',
            name: '91免费视频',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${window.location.origin}${__BASE_PATH__}/article/${video.id}`,
          },
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-14 md:pt-16">
          <div className="max-w-6xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: categoryNames[video.category] || video.category, href: `/category/${video.category}` },
                { label: video.title },
              ]}
            />
          </div>

          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2">
                  {/* Video Player Placeholder */}
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-foreground-950 mb-6">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors">
                        <i className="ri-play-fill text-background-50 text-3xl ml-1"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 text-white text-sm rounded-md">
                      {video.duration}
                    </div>
                  </div>

                  {/* Article Info */}
                  <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {video.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViewsFull(video.views)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {video.date}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {categoryNames[video.category] || video.category}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {video.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/60 whitespace-nowrap">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Article Content */}
                  <article className="prose prose-sm md:prose-base max-w-none text-foreground-700">
                    <p className="text-sm md:text-base leading-relaxed whitespace-pre-line">{video.content}</p>
                  </article>

                  {/* Share */}
                  <div className="mt-10 pt-6 border-t border-background-200/60">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-foreground-500">分享到：</span>
                      {['微信','微博','QQ'].map((platform) => (
                        <button
                          key={platform}
                          className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                          aria-label={`分享到${platform}`}
                        >
                          <i className={`ri-${platform === '微信' ? 'wechat' : platform === '微博' ? 'weibo' : 'qq'}-line text-lg`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-1">
                  <div className="sticky top-20">
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">相关推荐</h3>
                    <div className="space-y-4">
                      {relatedVideos.map((rv) => (
                        <Link
                          key={rv.id}
                          to={`/article/${rv.id}`}
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-40 flex-shrink-0 aspect-video rounded-lg overflow-hidden bg-background-100">
                            <img
                              src={rv.thumbnail}
                              alt={rv.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                              {rv.title}
                            </h4>
                            <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
                              <span>{formatViews(rv.views)}次</span>
                              <span>·</span>
                              <span>{rv.duration}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}